import { Box, Typography } from "@avenue-8/sidekick-ui";
import {
  ComparableProperty,
  PropertyPhoto,
  SideBySideComparisonItem,
  SideBySideComparisonSectionConfig,
  SideBySideComparisonSectionView,
} from "@cma/presentation-generation-logic";
import { ComparisonPropertyItem } from "./components/ComparisonPropertyItem";
import { SortableComparisonPropertySlide } from "./components/SortableComparisonPropertySlide";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AddComparablePropertyButton } from "./components/AddComparablePropertyButton";
import { PresentationSectionProps } from "../PresentationSectionProps";
import {
  SideBySideComparisonList,
  SideBySideComparisonHeader,
  NavButtonsContainer,
  NavButton,
  DragOverlayCard,
} from "./SideBySideComparison.styles";
import {
  Button,
  Divider,
  Menu,
  MenuItem,
  Tooltip,
} from "@avenue-8/sidekick-ui";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { SideBySideComparisonCarousel } from "./components/SideBySideComparisonCarousel";
import { useScrollableCarousel } from "./hooks/useScrollableCarousel";
import {
  DndContext,
  DragCancelEvent,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
interface Props
  extends PresentationSectionProps<
    SideBySideComparisonSectionView,
    SideBySideComparisonSectionConfig
  > {
  galleries: { [key: string]: PropertyPhoto[] };
  onOpenFindComparablePropertiesWidget: () => void;
  handleRemoveComparableProperty: (
    comparableProperty: ComparableProperty,
  ) => void;
  handleUpdateComparableProperty: (
    comparableProperty: ComparableProperty,
  ) => void;
  handleReorderComparableProperties: (
    comparableProperties: ComparableProperty[],
  ) => void;
}

export const SideBySideComparisonEdit = (props: Props) => {
  const {
    galleries,
    sectionView: { id, preferences, properties },
    sectionConfig,
    sourceData,
    onOpenFindComparablePropertiesWidget,
    onUpdateSection,
    handleRemoveComparableProperty,
    handleUpdateComparableProperty,
    handleReorderComparableProperties,
  } = props;

  const {
    containerRef,
    isBeginning,
    isEnd,
    scrollSliderLeft,
    scrollSliderRight,
    attachScrollListeners,
    detachScrollListeners,
    getContainerEl,
  } = useScrollableCarousel();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [activePropertyId, setActivePropertyId] = useState<string | null>(null);
  const [editingNotesPropertyId, setEditingNotesPropertyId] = useState<
    string | null
  >(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  useEffect(() => {
    attachScrollListeners();
    return () => detachScrollListeners();
  }, [attachScrollListeners, detachScrollListeners]);

  const handleRemovePropertyFromSection = (propertyId: string) => {
    const comparableProperty = sourceData.comparableProperties.find(
      (p) => p.id === propertyId,
    );

    if (
      comparableProperty &&
      comparableProperty.mlsId &&
      comparableProperty.mlsSource &&
      handleRemoveComparableProperty
    ) {
      handleRemoveComparableProperty(comparableProperty);
    }
  };

  const handleCancelEditNotes = () => {
    setEditingNotesPropertyId(null);
  };

  const handleSaveNotes = (rawNotes: string) => {
    const notes = rawNotes.trim() === "" ? null : rawNotes;
    const showNotesUpdate =
      notes && !sectionConfig.showNotes
        ? { showNotes: true as const }
        : undefined;

    const editingComparableProperty = sourceData.comparableProperties.find(
      (p) => p.id === editingNotesPropertyId,
    );

    if (editingComparableProperty) {
      handleUpdateComparableProperty({ ...editingComparableProperty, notes });
      // A note is useless if the field is hidden from the comparison grid —
      // surface it as soon as the first one is added, same as every other field.
      if (showNotesUpdate) {
        onUpdateSection({
          sectionConfig: { ...sectionConfig, ...showNotesUpdate },
        });
      }
      setEditingNotesPropertyId(null);
      return;
    }

    if (sourceData.featuredProperty.id === editingNotesPropertyId) {
      onUpdateSection({
        sectionConfig: showNotesUpdate
          ? { ...sectionConfig, ...showNotesUpdate }
          : sectionConfig,
        editableSourceData: { featuredProperty: { notes } },
      });
      setEditingNotesPropertyId(null);
    }
  };

  const { subjectProperty, comparableProperties } = useMemo(() => {
    if (!properties) return { subjectProperty: null, comparableProperties: [] };
    if (properties.length === 0)
      return { subjectProperty: null, comparableProperties: [] };
    const comparableProperties = properties.slice(1);
    const subjectProperty = properties[0];
    return { subjectProperty, comparableProperties };
  }, [properties]);

  // Local order to update immediately on drop, avoiding flash back to old position
  const [localComparableProperties, setLocalComparableProperties] =
    useState<SideBySideComparisonItem[]>(comparableProperties);

  useEffect(() => {
    setLocalComparableProperties(comparableProperties);
  }, [comparableProperties]);

  const comparablePropertyIds = useMemo(
    () => localComparableProperties.map((p) => p.id),
    [localComparableProperties],
  );

  const handlePropertyDragStart = (event: DragStartEvent) => {
    setActivePropertyId(event.active.id as string);
    detachScrollListeners();
  };

  const handlePropertyDragCancel = (_event: DragCancelEvent) => {
    setActivePropertyId(null);
    attachScrollListeners();
  };

  useEffect(() => {
    if (!activePropertyId) return;

    const EDGE_THRESHOLD = 80;
    const SCROLL_STEP = 220;
    const SCROLL_INTERVAL = 400;
    let interval: ReturnType<typeof setInterval> | null = null;

    const clearScroll = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handlePointerMove = (e: PointerEvent) => {
      const el = getContainerEl();
      if (!el) return;
      const rect = el.getBoundingClientRect();
      if (e.clientX > rect.right - EDGE_THRESHOLD) {
        if (!interval)
          interval = setInterval(() => {
            const el = getContainerEl();
            if (el) el.scrollLeft += SCROLL_STEP;
          }, SCROLL_INTERVAL);
      } else if (e.clientX < rect.left + EDGE_THRESHOLD) {
        if (!interval)
          interval = setInterval(() => {
            const el = getContainerEl();
            if (el) el.scrollLeft -= SCROLL_STEP;
          }, SCROLL_INTERVAL);
      } else {
        clearScroll();
      }
    };

    document.addEventListener("pointermove", handlePointerMove);
    return () => {
      document.removeEventListener("pointermove", handlePointerMove);
      clearScroll();
    };
  }, [activePropertyId, getContainerEl]);

  const handlePropertyDragEnd = (event: DragEndEvent) => {
    setActivePropertyId(null);
    attachScrollListeners();

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = comparablePropertyIds.indexOf(active.id as string);
    const newIndex = comparablePropertyIds.indexOf(over.id as string);

    // Update local order immediately to avoid flash back to old position
    setLocalComparableProperties((prev) => arrayMove(prev, oldIndex, newIndex));

    handleReorderComparableProperties(
      arrayMove(sourceData.comparableProperties, oldIndex, newIndex),
    );
  };

  const handleHideField = useCallback(
    (fieldKey: keyof SideBySideComparisonSectionConfig) => {
      onUpdateSection({
        sectionConfig: {
          ...sectionConfig,
          [fieldKey]: false,
        },
      });
    },
    [onUpdateSection, sectionConfig],
  );

  const handleAddField = useCallback(
    (fieldKey: keyof SideBySideComparisonSectionConfig) => {
      onUpdateSection({
        sectionConfig: {
          ...sectionConfig,
          [fieldKey]: true,
        },
      });
      setAnchorEl(null);
    },
    [onUpdateSection, sectionConfig],
  );

  const handleAddFieldClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const fieldLabels = {
    showPropertyType: "Property Type",
    showListingStatus: "Status",
    showListPrice: "List Price",
    showListDate: "List Date",
    showSoldPrice: "Sold Price",
    showSoldDate: "Sold Date",
    showYearBuilt: "Year Built",
    showNeighborhood: "Neighborhood",
    showLivingSpace: "Living Area",
    showBeds: "Beds",
    showBaths: "Baths",
    showLotSize: "Lot Size",
    showHOAFees: "HOA Fees",
    showGarageParkingSpaces: "Parking",
    showPricePerLivingSpaceSqft: "Price per Sqft",
    showDoormanAttendance: "Doorman",
    showRoomsTotal: "Total Rooms",
    showFlipTaxRemarks: "Flip Tax",
    showRecurringCosts: "Recurring Costs",
    showDaysOnMarket: "Days on Market",
    showCumulativeDaysOnMarket: "Cumulative Days on Market",
    showNotes: "Notes",
  } as const satisfies Partial<
    Record<keyof SideBySideComparisonSectionConfig, string>
  >;

  const defaultVisibleFields = new Set<string>([
    "showListPrice",
    "showSoldPrice",
    "showYearBuilt",
    "showListingStatus",
    "showNeighborhood",
    "showLivingSpace",
    "showBeds",
    "showBaths",
    "showPricePerLivingSpaceSqft",
    "showGarageParkingSpaces",
    "showRecurringCosts",
  ]);

  const isFieldVisible = (fieldKey: keyof typeof fieldLabels): boolean => {
    if (sectionConfig[fieldKey] !== undefined) {
      return sectionConfig[fieldKey] !== false;
    }
    if (preferences && preferences[fieldKey] !== undefined) {
      return preferences[fieldKey] !== false;
    }
    return defaultVisibleFields.has(fieldKey);
  };

  // Get all hidden fields from the complete list of available fields
  const hiddenFields = (
    Object.keys(fieldLabels) as Array<keyof typeof fieldLabels>
  ).filter((fieldKey) => !isFieldVisible(fieldKey));

  const subjectPropertyContent = subjectProperty ? (
    <ComparisonPropertyItem
      propertyData={subjectProperty}
      gallery={galleries[subjectProperty.id]}
      isSubjectProperty
      preferences={preferences}
      onHideField={handleHideField}
      isEditingNotes={subjectProperty.id === editingNotesPropertyId}
      onStartEditNotes={setEditingNotesPropertyId}
      onSaveNotes={handleSaveNotes}
      onCancelEditNotes={handleCancelEditNotes}
      editMode
    />
  ) : undefined;

  return (
    <Box
      id={id}
      pt={4}
      pb={6}
      px={4}
      marginX="auto"
      maxWidth={"lg"}
      textAlign="center"
    >
      <Typography variant="h5">Comparable Properties</Typography>

      <SideBySideComparisonList>
        <SideBySideComparisonHeader>
          <NavButtonsContainer>
            <NavButton disabled={isBeginning} onClick={scrollSliderLeft}>
              <ChevronLeftIcon />
            </NavButton>
            <NavButton disabled={isEnd} onClick={scrollSliderRight}>
              <ChevronRightIcon />
            </NavButton>
          </NavButtonsContainer>
          <Typography variant="overline">
            {comparableProperties.length}{" "}
            {comparableProperties.length === 1 ? "property" : "properties"}
          </Typography>
          <AddComparablePropertyButton
            onClick={onOpenFindComparablePropertiesWidget}
          />
        </SideBySideComparisonHeader>
        <DndContext
          sensors={sensors}
          onDragStart={handlePropertyDragStart}
          onDragEnd={handlePropertyDragEnd}
          onDragCancel={handlePropertyDragCancel}
        >
          <SortableContext
            items={comparablePropertyIds}
            strategy={horizontalListSortingStrategy}
          >
            <SideBySideComparisonCarousel
              containerRef={containerRef}
              subjectPropertyContent={subjectPropertyContent}
            >
              {localComparableProperties.map((property) => (
                <SortableComparisonPropertySlide
                  key={property.id}
                  id={property.id}
                >
                  {({
                    dragHandleRef,
                    dragHandleListeners,
                    dragHandleAttributes,
                  }) => (
                    <ComparisonPropertyItem
                      propertyData={property}
                      gallery={galleries[property.id]}
                      isSubjectProperty={false}
                      preferences={preferences}
                      onRemove={handleRemovePropertyFromSection}
                      onHideField={handleHideField}
                      isEditingNotes={property.id === editingNotesPropertyId}
                      onStartEditNotes={setEditingNotesPropertyId}
                      onSaveNotes={handleSaveNotes}
                      onCancelEditNotes={handleCancelEditNotes}
                      editMode
                      dragHandleRef={dragHandleRef}
                      dragHandleListeners={dragHandleListeners}
                      dragHandleAttributes={dragHandleAttributes}
                    />
                  )}
                </SortableComparisonPropertySlide>
              ))}
            </SideBySideComparisonCarousel>
          </SortableContext>
          <DragOverlay>
            {activePropertyId &&
              (() => {
                const property = localComparableProperties.find(
                  (p) => p.id === activePropertyId,
                );
                if (!property) return null;
                return (
                  <DragOverlayCard>
                    <ComparisonPropertyItem
                      propertyData={property}
                      gallery={galleries[property.id]}
                      isSubjectProperty={false}
                      preferences={preferences}
                    />
                  </DragOverlayCard>
                );
              })()}
          </DragOverlay>
        </DndContext>

        <Divider sx={{ width: "calc(100% - 2rem)", my: 3, mx: "auto" }} />

        <Tooltip
          title={hiddenFields.length === 0 ? "No fields available to add" : ""}
        >
          <Box display="flex" justifyContent="center">
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              size="small"
              onClick={handleAddFieldClick}
              disabled={hiddenFields.length === 0}
              aria-controls={open ? "add-field-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
            >
              Add Field
            </Button>
          </Box>
        </Tooltip>

        <Menu
          id="add-field-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleCloseMenu}
          MenuListProps={{
            "aria-labelledby": "add-field-button",
          }}
        >
          {hiddenFields.map((fieldKey) => (
            <MenuItem
              key={fieldKey}
              onClick={() =>
                handleAddField(
                  fieldKey as keyof SideBySideComparisonSectionConfig,
                )
              }
            >
              <Typography variant="body2">{fieldLabels[fieldKey]}</Typography>
            </MenuItem>
          ))}
        </Menu>
      </SideBySideComparisonList>
    </Box>
  );
};
